import * as dte from "../../../DtEventTypes";
import { FacetTypes, FacetsScope } from "../../../facets/Facets";
import { DtConstants } from "../../../schema/DtConstants";
import { ElementFlags, KeyFlags } from "../../../schema/dt-schema";
import { TandemCategories } from "../../../schema/TandemCategories";
import { nowSec } from "../../../streams/timeline/time";
import { StreamUtils } from "../../../streams/StreamUtils";
import { DtMap } from "./DtMap";
import { TRACE_ELEMENT_TYPE } from "../../../connectivity/SystemVisualizer";

import { av } from "@adsk/lmv";

const { ChangeTypes, StreamStates, StreamStatePriority, StreamAlertState2State, TicketPriority2Index } = DtConstants;

const connStatesEqual = (a, b) => {
    if (a === b) return true;
    if (a?.length !== b?.length) return false;
    return a.every((s) => b.includes(s));
};

// Note: We almost always want to sort in descending order, so the first element is the highest priority/state.
export const compareToTicket = (a, b) => (TicketPriority2Index[b?.priority] ?? -1) - (TicketPriority2Index[a?.priority] ?? -1);
export const compareToAlert = (a, b) => (b?.alertState ?? -1) - (a?.alertState ?? -1);

export class TrackedElementsProvider extends av.EventDispatcher {
    static DEFAULT_PREFS = Object.freeze({
        assets: true,
        tickets: true,
        connStates: null,
        filteredTCs: null,
    });

    facility;
    viewer;

    filteredElements = new DtMap();
    connStateByLabel = new DtMap();
    ticketsByLabel = new DtMap();
    alertsByLabel = new DtMap();
    #facilityLevelItem = null;

    hasDirtyReadings = false;
    hasDirtyTickets = false;

    /** @type {Set<number>} Limits elements by Tandem Category index. Missing means allow any. */
    filteredTCs = TrackedElementsProvider.DEFAULT_PREFS.filteredTCs;
    /** @type {string[]|} Connection states to show, if any. Undefined/null means show all */
    connStates = TrackedElementsProvider.DEFAULT_PREFS.connStates;
    isAssetsVisible = TrackedElementsProvider.DEFAULT_PREFS.assets;
    isTicketsVisible = TrackedElementsProvider.DEFAULT_PREFS.tickets;

	// Points to the same tracedElementTypeByModel Map in SystemVisualizer
	#tracedElementTypeByModel = null;

    /** @type {Set<object>} Currently-active consumers; the provider computes while at least one is active. */
    #active = new Set();

    constructor(facility, viewer) {
        super();

        this.facility = facility;
        this.viewer = viewer;
        this.boundOnReadingsChanged = this.#onReadingsChanged.bind(this);
        this.boundOnThemingChanged = this.#onThemingChanged.bind(this);
        this.boundOnTicketsChanged = this.#onTicketsChanged.bind(this);
		this.boundOnTraceElementsChanged = this.#onTraceElementsChanged.bind(this);
    }

    dispose() {
        this.filteredElements.clear();
        this.connStateByLabel.clear();
        this.ticketsByLabel.clear();
        this.alertsByLabel.clear();

        this.facility.eventTarget.removeEventListener(dte.DT_STREAMS_INFO_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.streamMgr.view.removeEventListener(dte.DT_TELEMETRY_INSTANT_READINGS_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.streamMgr.view.removeEventListener(dte.DT_TELEMETRY_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.eventTarget.removeEventListener(dte.DT_THEMING_CHANGED_EVENT, this.boundOnThemingChanged);
        this.facility.eventTarget.removeEventListener(dte.DT_TICKETS_CHANGED_EVENT, this.boundOnTicketsChanged);
        this.viewer.removeEventListener(dte.DT_TRACE_ELEMENTS_CHANGED_EVENT, this.boundOnTraceElementsChanged);
    }

    init() {
        this.facility.eventTarget.addEventListener(dte.DT_STREAMS_INFO_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.streamMgr.view.addEventListener(dte.DT_TELEMETRY_INSTANT_READINGS_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.streamMgr.view.addEventListener(dte.DT_TELEMETRY_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.eventTarget.addEventListener(dte.DT_THEMING_CHANGED_EVENT, this.boundOnThemingChanged);
        this.facility.eventTarget.addEventListener(dte.DT_TICKETS_CHANGED_EVENT, this.boundOnTicketsChanged);
		this.viewer.addEventListener(dte.DT_TRACE_ELEMENTS_CHANGED_EVENT, this.boundOnTraceElementsChanged);

        return this;
    }

    /**
     * Mark a consumer active (e.g. the HUD assets layer became visible, or the dt-client 1D viewer opened)
     * The provider is facility-scoped and shared, so it computes while any consumer is active
     * It refreshes when becoming active, since the cache may be stale.
     * @returns {Promise} Resolves once the refresh (if any) completes, so callers can await fresh data.
     */
    activate(consumer) {
        const wasActive = this.#active.size > 0;
        this.#active.add(consumer);
        return wasActive ? Promise.resolve() : this.forceRefresh();
    }

    /** Mark a consumer inactive. Safe to call when the consumer is not active. */
    deactivate(consumer) { this.#active.delete(consumer); }

    #isActive() { return this.#active.size > 0; }


    // Getters — the provider's read surface. Values are cached per (model, dbId).

    /** @returns {DtMap} The provided elements and their {@link ElementMetadata}. */
    getElements() { return this.filteredElements; }

    /** @returns {ElementMetadata|undefined} */
    getMetadata(model, dbId) { return this.filteredElements.get(model, dbId); }

    /** @returns {ConnState|undefined} Worst connection state for a host element. */
    getConnState(model, dbId) { return this.connStateByLabel.get(model, dbId); }

    /** @returns {Alert[]|undefined} Active alerts for a host element, worst-first. */
    getAlerts(model, dbId) { return this.alertsByLabel.get(model, dbId); }

    /** @returns {Ticket[]|null} Visible tickets hosted by an element. */
    getTickets(model, dbId) { return this.#getVisibleTicketsForHost(model, dbId); }

    /** @returns {FacilityLevelItem|null} The facility root rolled-up item, if any. */
    getFacilityLevelItem() { return this.#facilityLevelItem; }

    /** @returns {boolean} True when exactly one element across all models is traced. */
    isSingleTracedElement() {
        return this.#tracedElementTypeByModel?.size === 1
            && this.#tracedElementTypeByModel.values().next().value.size === 1;
    }

    /** @returns {boolean} */
    get isTicketsVisible() { return this.isTicketsVisible; }

    /** @returns {string[]|null} */
    get connStates() { return this.connStates; }

    /** @returns {Set<number>|null} */
    get filteredTCs() { return this.filteredTCs; }

    setState(prefs) {
        let changed = false;

        if (prefs.assets != null && this.isAssetsVisible !== prefs.assets) {
            this.isAssetsVisible = prefs.assets;
            changed = true;
        }

        if (prefs.connStates != null) {
            if (!connStatesEqual(prefs.connStates, this.connStates)) {
                this.connStates = prefs.connStates;
                changed = true;
            }
        } else if (this.connStates) {
            // default case - show all stream states
            this.connStates = null;
            changed = true;
        }

        if (prefs.tickets != null && this.isTicketsVisible !== prefs.tickets) {
            this.isTicketsVisible = prefs.tickets;
            changed = true;
        }

        // undefined -> no change, null -> clear filter, array -> set filter. State is saved as code not index.
        let filteredTCs = prefs.filteredTCs;
        if (filteredTCs !== undefined) {

            // Normalize incoming filter to Set of indexes or default.
            if (filteredTCs) {
                const indexes = new Set();
                for (const c of filteredTCs) {
                    const index = TandemCategories.code2index(c);
                    if (index > 0) indexes.add(index);
                }
                filteredTCs = indexes.size === TandemCategories.size ? null : indexes;
            }

            // Use default of all categories (assumes incoming state is unique)
            if (filteredTCs === null) {
                if (this.filteredTCs) {
                    this.filteredTCs = null;
                    changed = true;
                }
            } else {
                // Compare specific filters.
                if (!this.filteredTCs
                    || this.filteredTCs.size !== filteredTCs.size
                    || !this.filteredTCs.values().every((v) => filteredTCs.has(v))) {

                    // Setting a new filtered set.
                    this.filteredTCs = filteredTCs;
                    changed = true;
                }
            }
        }

        if (changed) {
            this.#updateFilteredElements();
        }
    }

    setStateFromView(state = {}) {
        this.setState({...TrackedElementsProvider.DEFAULT_PREFS, ...state});
    }

    getStateForView() {
        const state = { assets: this.isAssetsVisible, tickets: this.isTicketsVisible };
        if (this.connStates) state.connStates = this.connStates;
        // Saving as codes and using missing as default to allow reloading a configuration after a change to TCs.
        if (this.filteredTCs) state.filteredTCs = Array.from(this.filteredTCs, (i) => TandemCategories.index2code(i));

        return state;
    }

    /** Force a full refresh (used when the consumer becomes visible). Returns the recompute promise. */
    forceRefresh() {
        this.hasDirtyReadings = true;
        this.hasDirtyTickets = true;
        return this.#updateFilteredElements();
    }

    #emitChanged(redraw = false) {
        this.dispatchEvent({ type: dte.DT_TRACKED_ELEMENTS_CHANGED_EVENT, redraw });
    }

    /** @param {ElementPatch[]} patches */
    #emitPatched(patches) {
        this.dispatchEvent({ type: dte.DT_TRACKED_ELEMENTS_PATCHED_EVENT, patches });
    }

    #isConnStateVisible(state) {
        if (!state) return false;
        if (!this.connStates) return true;
        return this.connStates.includes(state);
    }

    #onThemingChanged({ scope }) {
        if (scope !== FacetsScope.Tickets) return;

        // force a re-compose to get new colors for tickets and so that deferred counts get them
        this.#emitChanged(true);
    }

    #onTicketsChanged() {
        this.hasDirtyTickets = true;
        this.#updateFilteredElements(true);
    }

    onIsolationChanged() { this.#updateFilteredElements(); }

    #getMetadataForElement(model, dbId) {
        let metadata;

        const tickets = this.#getVisibleTicketsForHost(model, dbId);
        if (this.isTicketsVisible && tickets) metadata = { tickets };

        const allAlerts = this.alertsByLabel.get(model, dbId);
        if (allAlerts) {
            const alerts = allAlerts.filter((a) => this.#isConnStateVisible(StreamAlertState2State[a.alertState]));
            if (alerts.length) (metadata ?? (metadata = {})).alerts = alerts;
        }

        const connState = this.connStateByLabel.get(model, dbId);
        if (connState && this.#isConnStateVisible(connState.state)) {
            (metadata ?? (metadata = {})).connState = connState;
        }

        return metadata;
    }

    #updateFacilityLevelItem() {
        const model = this.facility.getDefaultModel();
        if (!model) { this.#facilityLevelItem = null; return; }

        if (this.filteredTCs && !this.filteredTCs.has(TandemCategories.Facility)) {
            this.#facilityLevelItem = null;
            return;
        }

        const dbId = model.getRootId();
        const metadata = this.#getMetadataForElement(model, dbId);
        this.#facilityLevelItem = metadata ? { model, dbId, isSpatial: true, ...metadata } : null;
    }

    onModelChanged({ change }) {
        const defaultModelId = this.facility.getDefaultModelId();
        const { ctype, modelId, details } = change;

        switch (ctype) {
            case ChangeTypes.Mutate:
                if (modelId === defaultModelId) {
                    // #onTicketsChanged will handle ticket-only mutations
                    if (change.dbIds && change.dbIds.length > 0) {
                        const defaultModel = this.facility.getDefaultModel();
                        const dbId2flags = defaultModel.getData().dbId2flags;
                        const isChangeOnlyTickets = dbId2flags && change.dbIds.every(dbId =>
                            dbId2flags[dbId] === ElementFlags.Ticket
                        );

                        if (isChangeOnlyTickets) return;
                    }
                }
                this.#updateFilteredElements();
                return;
            case ChangeTypes.StreamAlert:
                if (modelId === defaultModelId && details.alertState !== details.previousState) {
                    this.#onAlertChanged(details);
                }
                return;
            default:
                return;
        }
    }

    #onAlertChanged({ alertState, streamId, substreamEncId }) {
        if (!this.#isActive()) return;

        const stream = this.facility.streamMgr.getAllStreamInfosFromCache().find((s) => s.fullId === streamId);
        if (stream?.hostElement == null) return;

        const { model, hostId } = stream.hostElement;
        const alerts = this.alertsByLabel.get(model, hostId);
        let updatedAlerts;

        // Assets had no prior active alerts.
        if (!alerts) {
            if (alertState > 0) {
                // First alert for this asset
                updatedAlerts = [{ alertState, streamId, substreamEncId }];
            } else {
                return;
            }
        } else {
            const priorIdx = alerts.findIndex((a) => a.streamId === streamId && a.substreamEncId === substreamEncId);
            if (priorIdx >= 0) {
                // Host has other alerts, but never for this stream.
                if (alertState > 0) {
                    // update in place
                    alerts[priorIdx].alertState = alertState;
                    updatedAlerts = alerts.sort(compareToAlert);
                } else {
                    // removing an element preserved the order.
                    alerts.splice(priorIdx, 1);
                    updatedAlerts = alerts.length ? alerts : null; // clear if no more alert
                }
            } else {
                // Host has other alerts, but never for this stream.
                if (alertState > 0) {
                    alerts.push({ alertState, streamId, substreamEncId });
                    updatedAlerts = alerts.sort(compareToAlert);
                } else {
                    return;
                }
            }
        }

        // Inner alert structure
        this.alertsByLabel.set(model, hostId, updatedAlerts);

        if (model === this.facility.getDefaultModel() && hostId === model.getRootId()) {
            this.#updateFacilityLevelItem();
        }

        const visibleAlerts = updatedAlerts?.filter((a) => this.#isConnStateVisible(StreamAlertState2State[a.alertState]));
        const alertsToShow = visibleAlerts?.length ? visibleAlerts : null;

        // Metadata for assets
        const metadata = this.filteredElements.get(model, hostId);

        // Rebuild if the element is currently not shown or should no longer be shown,
        // such as when the alert state was visible previously, but not anymore after and vice versa.
        // otherwise patch it in place.
        if (alertsToShow) {
            if (!metadata) {
                this.#updateFilteredElements();
                return;
            }
            metadata.alerts = alertsToShow;
            this.#emitPatched([{ model, dbId: hostId, alerts: alertsToShow }]);
        } else {
            if (metadata?.alerts) {
                this.#updateFilteredElements();
                return;
            }
            this.#emitPatched([{ model, dbId: hostId, alerts: null }]);
        }
    }

    async #updateTickets() {
        if (!this.#isActive()) return;

        const defaultModel = this.facility.getDefaultModel();
        if (!defaultModel) return;

        const tickets = await defaultModel.getTickets();

        this.ticketsByLabel.clear();

        for (const ticket of tickets) {
            if (!ticket.hosts?.length) continue;

            for (const hostElement of ticket.hosts) {
                const hostTickets = this.ticketsByLabel.getOrSet(hostElement.model, hostElement.hostId, []);
                hostTickets.push(ticket);
            }
        }

        this.ticketsByLabel.forEach((hostTickets) => hostTickets.sort(compareToTicket));

        this.hasDirtyTickets = false;
    }

    #getVisibleTicketsForHost(model, hostId) {
        const defaultModel = this.facility.getDefaultModel();
        const tickets = this.ticketsByLabel.get(model, hostId);
        if (!defaultModel || !tickets) return;

        const visible = tickets.filter((t) => defaultModel.visibilityManager.isNodeVisible(t.dbId));
        return visible.length ? visible : null;
    }

    async #onReadingsChanged({ updated, instant }) {
        if (!this.#isActive()) return;

        if (!updated) {
            // if details on what was updated are missing, recalculate everything.
            this.hasDirtyReadings = true;
            return this.#updateFilteredElements();
        }

        const streams = this.facility.streamMgr.getAllStreamInfosFromCache();
        const toUpdate = new DtMap();

        // the IOT changes are most likely to a single <conn, attr> pair, but that might change in the future.
        // we need 2 passes since we don't have a Map<hostElement, streams[]>
        for (let i = 0; i < updated.length; i += 2) {
            const stream = streams.find((s) => s.dbId === updated[i]);
            if (stream?.hostElement) {
                const { model, hostId } = stream.hostElement;
                const prior = this.connStateByLabel.get(model, hostId);
                toUpdate.set(model, hostId, prior);
                this.connStateByLabel.delete(model, hostId);
            }
        }

        // Updates the per-host connection state, for each affected host element.
        const time = this.facility.streamMgr.view.time.resolve().unix();
        const commonNow = nowSec();
        for (let stream of streams) {
            if (!stream.hostElement) continue;

            const { hostId, model } = stream.hostElement;
            if (!toUpdate.get(model, hostId)) continue;

            const priorWorst = this.connStateByLabel.get(model, hostId);
            const state = StreamUtils.getConnectionStateFromInstantData(stream, instant, time, commonNow);

            if (!priorWorst || StreamStatePriority[state] > StreamStatePriority[priorWorst.state]) {
                this.connStateByLabel.set(model, hostId, { stream, state });
            }
        }

        // If stream states are explicitly hidden, we don't need to update the UIs
        if (this.connStates?.length === 0) return;

        let needsRebuild = false;
        const patches = [];
        toUpdate.forEach((prior, model, hostId) => {
            // Return early if we need a rebuild
            if (needsRebuild) return;

            const active = this.connStateByLabel.get(model, hostId);
            const showActive = this.#isConnStateVisible(active?.state);
            const priorShown = this.#isConnStateVisible(prior?.state);

            // already shown/hidden and the new status is still the same, no need to update
            if (prior?.state === active?.state && priorShown === showActive) return;

            const metadata = this.filteredElements.get(model, hostId);

            if (model === this.facility.getDefaultModel() && hostId === model.getRootId()) {
                this.#updateFacilityLevelItem();
            }

            if (showActive) {
                if (!metadata) {
                    // set needsRebuild to true if the element is currently not shown or should no longer be shown,
                    // such as when the connection state was visible previously, but not anymore after and vice versa.
                    needsRebuild = true;
                    return;
                }
                metadata.connState = active;
                patches.push({ model, dbId: hostId, connState: active });
            } else {
                if (metadata?.connState) {
                    needsRebuild = true;
                    return;
                }
                patches.push({ model, dbId: hostId, connState: null });
            }
        });

        if (needsRebuild) return this.#updateFilteredElements();
        if (patches.length) this.#emitPatched(patches);
    }

    async #updateFromReadings() {
        const streamMgr = this.facility.streamMgr;
        const readings = await streamMgr.view.getInstantReadings();
        if (!readings) return;

        // Current time configuration
        const time = streamMgr.view.time.resolve().unix();
        const commonNow = nowSec();

        // Worst state by host {WARN, ALERT, OFFLINE}
        const streams = streamMgr.getAllStreamInfosFromCache();
        this.connStateByLabel.clear();

        // TEMP: Move to dedicated alert update handler
        const alerts = await streamMgr.getCurrentAlerts();
        this.alertsByLabel.clear();

        for (const stream of streams) {
            if (!stream.hostElement) continue;

            const { hostId, model } = stream.hostElement;

            // Determine the worst connection state for the host
            const priorWorst = this.connStateByLabel.get(model, hostId);
            const state = StreamUtils.getConnectionStateFromInstantData(stream, readings, time, commonNow);

            if (!priorWorst || StreamStatePriority[state] > StreamStatePriority[priorWorst.state]) {
                // First or higher priority state for the host
                this.connStateByLabel.set(model, hostId, { stream, state });
            }

            // Obtain alerts for hosted connections
            const alertByAttrId = alerts?.[stream.fullId];
            if (alertByAttrId) {
                const partialAlerts = this.alertsByLabel.getOrSet(model, hostId, []);
                for (const attrId in alertByAttrId) {
                    const { currentState } = alertByAttrId[attrId];
                    if (StreamAlertState2State[currentState] === StreamStates.Normal) continue; // skip Normal
                    partialAlerts.push({ alertState: currentState, streamId: stream.fullId, substreamEncId: attrId });
                }
            }
        }

        // Sort once we know all possible alerts for a host.
        this.alertsByLabel.forEach((alerts) => alerts.sort(compareToAlert));

        this.hasDirtyReadings = false;
    }

    #onTraceElementsChanged({ tracedElementTypeByModel }) {
        this.#tracedElementTypeByModel = tracedElementTypeByModel;
        this.#updateFilteredElements();
    }

    * iterateElements() {
        for (const byModel of this.facility.facetsManager.idsByModelFiltered || []) {
            yield byModel;
        }

        if (this.#tracedElementTypeByModel) {
            // idsByModelFiltered includes all elements that passed through the facet filters,
            // but some elements in the traces may have been filtered out since traces are displayed regardless of
            // facet filters. We need to also iterate over tracedElementTypeByModel to get all elements that can be labeled
            for (const [model, byDbId] of this.#tracedElementTypeByModel) {
                yield [model, byDbId.keys()];
            }
        }
    }

    async #updateFilteredElements(redraw = false) {
        if (!this.#isActive()) return;

        const dependencies = [];
        if (this.hasDirtyReadings) dependencies.push(this.#updateFromReadings());
        if (this.hasDirtyTickets) dependencies.push(this.#updateTickets());
        await Promise.all(dependencies);

        //console.time('TrackedElementsProvider#updateFilteredElements :');
        this.filteredElements.clear();
        for (const [model, dbIds] of this.iterateElements()) {
            const data = model.getData();
            if (!data) continue;

            const byDbId = this.filteredElements.map.get(model) || new Map();
            for (const dbId of dbIds) {
                // Skip if already processed
                if (byDbId.has(dbId)) continue;

                const flags = data.dbId2flags[dbId];

                // Spatial elements can still be assets, but will never show as an asset label (only deferred).
                const isSpatial = flags === ElementFlags.Room || flags === ElementFlags.Level || flags === ElementFlags.Location;
                // Allows generic assets to be shown in 1D, when noBim flag is set
                const isAsset = model.loadContext.noBim && model.isAsset(dbId);
                if (!isSpatial && !isAsset && (flags & ElementFlags.AllLogicalMask) !== KeyFlags.Physical) {
                    // Skip logical elements (connection/tickets/generic)
                    continue;
                }

                // Ensure labels are shown for trace start/end/POI elements
                // For other traced elements, we use the same logic as for non-traced elements
                const tracedType = this.#tracedElementTypeByModel?.get(model)?.get(dbId);
                const canBeFiltered = !tracedType || tracedType <= TRACE_ELEMENT_TYPE.POI;

                if (canBeFiltered && this.filteredTCs && !this.filteredTCs.has(data.dbId2TandemCategory[dbId])) {
                    // The Tandem Category filter applies on both assets or host of connections/alerts/tickets.
                    continue;
                }

                let metadata = this.#getMetadataForElement(model, dbId);

                const needsTraceLabel = tracedType >= TRACE_ELEMENT_TYPE.POI;

                // later code doesn't care about strict assets, but if it does, we could add a differentiator here.
                if (this.isAssetsVisible && !metadata && (needsTraceLabel || model.isAsset(dbId))) metadata = {};

                if (metadata) {
                    metadata.isSpatial = isSpatial;
                    metadata.tracedType = tracedType;
                    byDbId.set(dbId, metadata);
                }
            }

            if (byDbId.size) {
                this.filteredElements.map.set(model, byDbId);
            }
        }
        //console.timeEnd('TrackedElementsProvider#updateFilteredElements :');

        const options = {
            modelsByUrn: this.facility.facetsManager.models.reduce((dict, m) => { dict[m.urn()] = m; return dict; }, {})
        };

        const levelsDef = this.facility.facetsManager.getFacetDef(FacetTypes.levels);
        const levelFacets = levelsDef.batchClassify(this.filteredElements.asIdSetIterator(), options);
        for (const { id, ids } of levelFacets.facets) {
            for (const [model, dbIds] of ids) {
                const byDbId = this.filteredElements.map.get(model);
                for (const dbId of dbIds) {
                    const metadata = byDbId.get(dbId);
                    metadata.levelsFacetId ??= [];
                    metadata.levelsFacetId.push(id);
                }
            }
        }
        
        const spacesDef = this.facility.facetsManager.getFacetDef(FacetTypes.spaces);
        const spaceFacets = spacesDef.batchClassify(this.filteredElements.asIdSetIterator(), options);
        for (const { id, ids } of spaceFacets.facets) {
            for (const [model, dbIds] of ids) {
                const byDbId = this.filteredElements.map.get(model);
                for (const dbId of dbIds) {
                    const metadata = byDbId.get(dbId);
                    metadata.spacesFacetId ??= [];
                    metadata.spacesFacetId.push(id);
                }
            }
        }
        
        const locationsDef = this.facility.facetsManager.getFacetDef(FacetTypes.locations);
        if (locationsDef) {
            const locationFacets = locationsDef.batchClassify(this.filteredElements.asIdSetIterator(), options);
            for (const { id, ids, isRoom, parentLevelId } of locationFacets.facets) {
                for (const [model, dbIds] of ids) {
                    const byDbId = this.filteredElements.map.get(model);
                    for (const dbId of dbIds) {
                        const metadata = byDbId.get(dbId);
                        if(isRoom) {
                            metadata.spacesFacetId ??= [];
                            metadata.spacesFacetId.push(id);
                            metadata.levelsFacetId ??= [];
                            metadata.levelsFacetId.push(parentLevelId);
                        } else {
                            metadata.levelsFacetId ??= [];
                            metadata.levelsFacetId.push(id);
                        }
                    }
                }
            }
        }

        this.#updateFacilityLevelItem();
        this.#emitChanged(redraw);
    }
}
